import { SKI_LOCATIONS, SURF_LOCATIONS } from "@/lib/conditions/locations";
import { SAILING_LOCATIONS } from "@/lib/conditions/sailing-scoring";
import { sportLabel } from "@/lib/conditions/sport-meta";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import { addDaysToIsoDate, localDateInTimeZone, zonedLocalDateTimeToUtc } from "@/lib/public-events/time";
import { isRouteJourneyId, routeJourneyId } from "@/lib/planner/route-outcomes";
import type { SavedRoute } from "@/lib/planner/saved-route";
import type { CompanionPlanPlaceView } from "./plan-model";
import type { CompanionRecommendationCandidate } from "./recommendation-types";

export const DAY_OUT_PARAM = "outing";
export const DAY_OUT_MAX_BYTES = 768;

/** A public selection, never a route snapshot, location fix, note or consent. */
export type DayOut = {
  v: 1;
  slug: string;
  sport: string;
  timezone: string;
  startAt: string;
  endAt: string;
  journeyId?: string;
};

const MOUNTAIN_SPORTS = ["ski", "piste", "off-piste", "touring", "mtb"];
const WATER_SPORTS = ["surf", "bodyboarding", "wind", "foil", "downwind-foil", "wing-foil", "diving", "swim", "swimming", "sup", "sup-touring", "sup-surf", "sup-downwind"];
const SAILING_SPORTS = ["sailing", "sailing-club", "sailing-hp"];

export function dayOutPlace(selection: Pick<DayOut, "slug" | "sport">) {
  const catalogue = MOUNTAIN_SPORTS.includes(selection.sport) ? SKI_LOCATIONS
    : SAILING_SPORTS.includes(selection.sport) ? SAILING_LOCATIONS.map((place) => ({ id: place.slug, name: place.name }))
      : WATER_SPORTS.includes(selection.sport) ? SURF_LOCATIONS : [];
  const place = catalogue.find((item) => item.id === selection.slug);
  return place ? { slug: place.id, name: place.name } : null;
}

export function parseDayOut(raw: unknown): DayOut | null {
  if (typeof raw !== "string" || !raw || new TextEncoder().encode(raw).length > DAY_OUT_MAX_BYTES) return null;
  try {
    const input: unknown = JSON.parse(raw);
    if (!input || typeof input !== "object" || Array.isArray(input)) return null;
    const value = input as Record<string, unknown>;
    if (value.v !== 1 || typeof value.slug !== "string" || typeof value.sport !== "string"
      || typeof value.timezone !== "string" || value.timezone.length > 80
      || typeof value.startAt !== "string" || typeof value.endAt !== "string") return null;
    if (!dayOutPlace({ slug: value.slug, sport: value.sport })) return null;
    const dates = [value.startAt, value.endAt];
    if (dates.some((date) => !/^20\d{2}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(date)
      || !Number.isFinite(Date.parse(date)) || new Date(date).toISOString().slice(0, 19) !== date.slice(0, 19))) return null;
    const span = Date.parse(value.endAt) - Date.parse(value.startAt);
    if (span <= 0 || span > 24 * 60 * 60 * 1000) return null;
    // Validate an IANA zone and keep local dates independent of the browser.
    localDateInTimeZone(new Date(value.startAt), value.timezone);
    const timezone = new Intl.DateTimeFormat("en-GB", { timeZone: value.timezone }).resolvedOptions().timeZone;
    if (value.journeyId !== undefined && (typeof value.journeyId !== "string" || !isRouteJourneyId(value.journeyId)
      || !["ski", "piste", "mtb"].includes(value.sport))) return null;
    return {
      v: 1, slug: value.slug, sport: value.sport, timezone,
      startAt: new Date(value.startAt).toISOString(), endAt: new Date(value.endAt).toISOString(),
      ...(typeof value.journeyId === "string" ? { journeyId: value.journeyId } : {}),
    };
  } catch { return null; }
}

export function dayOutParam(selection: DayOut | null | undefined): string | null {
  if (!selection) return null;
  const clean = parseDayOut(JSON.stringify(selection));
  return clean ? JSON.stringify(clean) : null;
}

export function withDayOut(href: string, selection: DayOut | null | undefined): string {
  if (!selection || !href.startsWith("/") || href.startsWith("//")) return href;
  const value = dayOutParam(selection);
  if (!value) return href;
  const url = new URL(href, "https://peakwave.co");
  url.searchParams.set(DAY_OUT_PARAM, value);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function dayOutLocal(selection: DayOut) {
  const time = (instant: string) => new Intl.DateTimeFormat("en-GB", {
    timeZone: selection.timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(new Date(instant));
  return { date: localDateInTimeZone(new Date(selection.startAt), selection.timezone),
    endDate: localDateInTimeZone(new Date(selection.endAt), selection.timezone),
    startTime: time(selection.startAt), endTime: time(selection.endAt) };
}

export function dayOutStatus(selection: DayOut, now = new Date()): "upcoming" | "current" | "ended" {
  return now.getTime() >= Date.parse(selection.endAt) ? "ended"
    : now.getTime() < Date.parse(selection.startAt) ? "upcoming" : "current";
}

export function canStartDayOutRoute(selection: DayOut, now = new Date()): boolean {
  return dayOutStatus(selection, now) !== "ended"
    && dayOutLocal(selection).date === localDateInTimeZone(now, selection.timezone);
}

export function dayOutSportLabel(sport: string): string {
  // Exact SUP profiles remain subject to the destination's existing access gate.
  return isSupProfileKey(sport) ? "SUP" : sport === "ski" ? "Ski" : sport === "sailing" ? "Sailing" : sportLabel(sport);
}

export function dayOutConditionsHref(selection: DayOut): string {
  const path = SAILING_SPORTS.includes(selection.sport)
    ? `/conditions/sailing/${selection.slug}` : `/conditions/${selection.slug}`;
  return withDayOut(`${path}?${new URLSearchParams({ sport: selection.sport })}`, selection);
}

export function dayOutPlannerHref(selection: DayOut): string {
  if (["ski", "piste", "mtb"].includes(selection.sport)) {
    return withDayOut(`/conditions/${selection.slug}/planner?${new URLSearchParams({ sport: selection.sport === "mtb" ? "mtb" : "ski", ...(selection.journeyId ? { saved: "1" } : {}) })}`, selection);
  }
  const url = new URL(dayOutConditionsHref(selection), "https://peakwave.co");
  if (!MOUNTAIN_SPORTS.includes(selection.sport)) { url.searchParams.set("planner", "1"); url.hash = "session-planner"; }
  return `${url.pathname}${url.search}${url.hash}`;
}

export function dayOutFromLocal(input: { slug: string; sport: string; timezone: string; date: string; startTime: string; endTime: string; journeyId?: string }): DayOut | null {
  const start = zonedLocalDateTimeToUtc(`${input.date}T${input.startTime}`, input.timezone);
  let endDate = input.date;
  let endTime = input.endTime;
  if (endTime === "24:00") { try { endDate = addDaysToIsoDate(endDate, 1); } catch { return null; } endTime = "00:00"; }
  const end = zonedLocalDateTimeToUtc(`${endDate}T${endTime}`, input.timezone);
  if (!start || !end) return null;
  return parseDayOut(JSON.stringify({ v: 1, slug: input.slug, sport: input.sport, timezone: input.timezone,
    startAt: start.toISOString(), endAt: end.toISOString(), ...(input.journeyId ? { journeyId: input.journeyId } : {}) }));
}

export function dayOutFromSavedRoute(saved: SavedRoute): DayOut | null {
  return dayOutFromLocal({ slug: saved.slug, sport: saved.preferences.sport, timezone: saved.plan.area.timezone,
    date: saved.plan.scoredDate, startTime: saved.preferences.startTime, endTime: saved.preferences.endTime,
    journeyId: routeJourneyId(saved) });
}

export function dayOutFromRecommendation(candidate: CompanionRecommendationCandidate): DayOut | null {
  return parseDayOut(JSON.stringify({ v: 1, slug: candidate.slug, sport: candidate.sportKey,
    timezone: candidate.timezone, startAt: candidate.remainingWindowStartAt ?? candidate.nextWindowStartAt,
    endAt: candidate.remainingWindowEndAt ?? candidate.nextWindowEndAt }));
}

export function dayOutFromPlanPlace(place: CompanionPlanPlaceView): DayOut | null {
  const window = place.score?.usefulWindow?.match(/^(\d{2}:\d{2}) to (\d{2}:\d{2})$/);
  if (!window || !place.score) return null;
  return dayOutFromLocal({ slug: place.slug, sport: place.selectedSport, timezone: place.timezone,
    date: place.score.date, startTime: window[1], endTime: window[2] });
}

export function dayOutRouteMatches(selection: DayOut, saved: SavedRoute | null): boolean {
  if (!selection.journeyId || !saved) return false;
  const actual = dayOutFromSavedRoute(saved);
  return Boolean(actual && actual.journeyId === selection.journeyId && actual.slug === selection.slug
    && actual.startAt === selection.startAt && actual.endAt === selection.endAt
    && actual.timezone === selection.timezone && (actual.sport === selection.sport || actual.sport === "ski" && selection.sport === "piste"));
}

/** Never reinterpret a selected day as the planner's default day. */
export function dayOutPlannerTiming(selection: DayOut, timezone: string, now = new Date()) {
  if (dayOutStatus(selection, now) === "ended") return null;
  const local = dayOutLocal(selection);
  let today: string;
  try {
    if (new Intl.DateTimeFormat("en-GB", { timeZone: timezone }).resolvedOptions().timeZone !== selection.timezone) return null;
    today = localDateInTimeZone(now, timezone);
  } catch { return null; }
  const date = local.date === today ? "today" : local.date === addDaysToIsoDate(today, 1) ? "tomorrow" : null;
  if (!date || local.date !== local.endDate && local.endTime !== "00:00") return null;
  return { date, startTime: local.startTime, endTime: local.endDate !== local.date ? "24:00" : local.endTime } as const;
}
